import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase, faCalendarAlt, faLocationDot, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

export default function Experience() {
  const experiences = [
    {
      role: "Staff Cloud Platform Engineer & Architect",
      company: "Enterprise Cloud Modernization",
      period: "Recent - Present",
      location: "India · Global Delivery",
      badge: "Platform & Cloud Architecture",
      highlights: [
        "Led large-scale AWS-to-GCP cloud migration and monolithic application decomposition into composable cloud-native microservices on GKE and Cloud Run.",
        "Architected Atlantis pull-request automation for Terraform with custom Terragrunt orchestration, enabling declarative git-driven infrastructure changes.",
        "Engineered multi-region disaster recovery and backup strategies guaranteeing business continuity under full regional outage scenarios.",
        "Built centralized observability platforms with GCP Cloud Monitoring, Prometheus, Grafana, custom log-based metrics, and alerting SLOs.",
        "Spearheaded FinOps cost governance via custom GKE CronJobs and automated resource scheduling, significantly reducing cloud spend.",
        "Orchestrated cloud security remediation using Google Cloud SCC, Wiz CSPM, CrowdStrike Falcon, and Trivy security scanning in GitLab CI/CD.",
        "Integrated AI-assisted engineering workflows leveraging Claude Code, Gemini, and GitHub Copilot to streamline platform troubleshooting, scripting, and IaC generation."
      ],
      skills: ["GCP", "Kubernetes", "Terraform", "Terragrunt", "Atlantis", "GitOps", "Wiz", "Claude Code", "Prometheus"]
    },
    {
      role: "Senior DevOps Engineer / Senior SRE",
      company: "UST · Client: Equinix Inc.",
      period: "Enterprise Engagement",
      location: "Australia Region · Multi-Region",
      badge: "SRE & Multi-Cloud Infrastructure",
      highlights: [
        "Provisioned high-availability GCP infrastructure in the Australia region using Terraform and Jenkins CI/CD automation.",
        "Architected resilient compute environments using Managed Instance Groups (MIGs) across availability zones, Cloud SQL, and Internal Load Balancers.",
        "Implemented enterprise zero-trust secret management integrating HashiCorp Vault with Jenkins pipelines and compute infrastructure.",
        "Maintained Nexus build artifact lifecycle and standardized automated deployment release workflows."
      ],
      skills: ["GCP", "HashiCorp Vault", "Terraform", "Jenkins", "Cloud SQL", "Nexus", "MIGs", "Linux"]
    },
    {
      role: "Cloud Automation & Systems Operations Engineer",
      company: "Enterprise Infrastructure Services",
      period: "Foundation & Growth",
      location: "India",
      badge: "Infrastructure & Automation",
      highlights: [
        "Developed automated multi-cloud backup and disaster recovery strategies in Azure, ensuring zero data loss and strict compliance SLA adherence.",
        "Automated operational AWS infrastructure tasks with Jenkins pipelines, Bash scripting, and Python automation tools.",
        "Supported mainframe-to-UNIX modernization programs, automating application deployment pipelines and Ansible configuration provisioning.",
        "Mentored junior engineers on Docker, containerization, Linux systems administration, and cloud-native operational best practices."
      ],
      skills: ["Microsoft Azure", "AWS", "Ansible", "Docker", "Python", "Bash", "Linux", "CI/CD"]
    }
  ];

  return (
    <section id="experience" className="py-16 md:py-24 text-white border-t border-zinc-800/80">
      {/* Section Header */}
      <div className="max-w-3xl mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-teal-500/10 text-teal-300 border border-teal-500/30 uppercase tracking-wider mb-3">
          Career Track Record
        </div>
        <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-white">
          Work Experience &amp; Leadership
        </h2>
        <p className="mt-2 text-base text-gray-400">
          Over 14 years architecting resilient platform engineering solutions, driving cloud migrations, and managing enterprise infrastructure at scale.
        </p>
      </div>

      {/* Experience Timeline */}
      <div className="relative border-l-2 border-zinc-800 ml-3.5 sm:ml-8 space-y-10 sm:space-y-12">
        {experiences.map((exp, idx) => (
          <div key={idx} className="relative pl-5 sm:pl-10 group">
            {/* Timeline Node */}
            <div className="absolute -left-[17px] top-1.5 w-8 h-8 rounded-full bg-dark-500 border-2 border-teal-500 flex items-center justify-center text-teal-400 group-hover:bg-teal-500 group-hover:text-dark-900 transition-colors shadow-md shadow-teal-500/20">
              <FontAwesomeIcon icon={faBriefcase} className="text-xs" />
            </div>

            {/* Experience Card */}
            <div className="p-5 sm:p-8 rounded-2xl bg-dark-200/50 border border-zinc-800 hover:border-teal-500/40 transition-all duration-300 hover:bg-dark-200/80">
              {/* Header Info */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white group-hover:text-teal-300 transition-colors">
                    {exp.role}
                  </h3>
                  <div className="text-base font-semibold text-gray-300 mt-0.5">
                    {exp.company}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-gray-400">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zinc-800/80 border border-zinc-700/60">
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-teal-400" />
                    <span>{exp.period}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zinc-800/80 border border-zinc-700/60">
                    <FontAwesomeIcon icon={faLocationDot} className="text-teal-400" />
                    <span>{exp.location}</span>
                  </span>
                </div>
              </div>

              {/* Highlights Bullet List */}
              <ul className="space-y-2.5 text-sm sm:text-base text-gray-300 leading-relaxed mb-6">
                {exp.highlights.map((point, pIdx) => (
                  <li key={pIdx} className="flex items-start gap-3">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-teal-400 text-sm mt-1 shrink-0" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>

              {/* Tech Stack Pills */}
              <div className="flex flex-wrap gap-2 pt-4 border-t border-zinc-800/80">
                {exp.skills.map((skill, sIdx) => (
                  <span
                    key={sIdx}
                    className="px-2.5 py-1 text-xs font-medium rounded-md bg-dark-300/80 text-teal-300 border border-teal-500/20"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
